import { DetectDevicesService } from '@fi-shared/fi-shared-services/detect-devices.service';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Statistic2ReportParameterEditDialogComponent } from '@fi-app/statistic2/statistic2-report-parameter-edit/statistic2-report-parameter-edit-dialog/statistic2-report-parameter-edit-dialog.component';
import { Statistic2HelperService } from '@fi-app/statistic2/statistic2-helper.service';
import { Statistic2Service } from '@fi-app/statistic2/statistic2.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { BehaviorSubject } from 'rxjs';
import { Statistic2ReportParameterModel } from '@fi-app/statistic2/statistic2-report-parameter-list/statistic2-report-parameter-model';
import { IStatistic2ReportParameter } from '@fi-app/statistic2/statistic2';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Statistic2AddExistingReportDialogComponent } from '@fi-app/statistic2/statistic2-header/statistic2-add-existing-report-dialog/statistic2-add-existing-report-dialog.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';

@Component({
    selector: 'fi-statistic2-header-add-report',
    template: `
        @if (isDashboardTabSelected) {
          <button
            mat-raised-button
            class="BrandedBackgroundColor fi-header-button"
            [matMenuTriggerFor]="dashboardAddReportMatMenu"
            >
            <span class="fas fa-plus"></span>
          </button>
        }
        
        @if (isReportsTabSelected) {
          <button
            mat-raised-button
            class="BrandedBackgroundColor fi-header-button"
            [disabled]="isMobileDevice()"
            (click)="onNewReportClick($event)"
            >
            <mat-icon class="fas fa-plus"></mat-icon>
          </button>
        }
        
        <mat-menu class="fi-mat-more-menu checklist-menu" #dashboardAddReportMatMenu="matMenu">
          <button mat-menu-item [disabled]="isMobileDevice()" (click)="onNewReportClick($event)">
            {{ 'statistic.create_new_report' | fiAppText }}
          </button>
          <button
            mat-menu-item
            (click)="onAddExistingReportClick($event)"
            [disabled]="isAddExistingReportDisabled$ | async"
            >
            {{ 'statistic.add_existing_report' | fiAppText }}
          </button>
        </mat-menu>
        `,
    styleUrls: ['./statistic2-header-add-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-statistic2-header-add-report' },
    standalone: false,
})
export class Statistic2HeaderAddReportComponent implements OnInit, OnDestroy {
    @Input() isDashboardTabSelected: boolean;
    @Input() isReportsTabSelected: boolean;

    isAddExistingReportDisabled$ = new BehaviorSubject<boolean>(true);
    private availableReportParameters: IStatistic2ReportParameter[];

    constructor(
        private statistic2Service: Statistic2Service,
        private statistic2HelperService: Statistic2HelperService,
        private fiLoadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private detectDeviceService: DetectDevicesService,
        private appTextService: AppTextService,
    ) {}

    ngOnInit() {
        this.getAvailableReportParameters();
        this.statistic2Service.reportParametersUpdated$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.getAvailableReportParameters();
        });
    }

    isMobileDevice() {
        return this.detectDeviceService.isMobileSmall();
    }

    private getAvailableReportParameters() {
        this.statistic2Service
            .getReportParameters(new Statistic2ReportParameterModel(false))
            .then((reportParameters) => {
                this.isAddExistingReportDisabled$.next(reportParameters.length === 0);
                this.availableReportParameters = reportParameters;
            });
    }

    onNewReportClick($event) {
        const reportParameter = this.statistic2HelperService.getParameterForNewReport();

        this.statistic2HelperService
            .showEditReportDialog(
                'statistic.new_statistics_report',
                Statistic2ReportParameterEditDialogComponent,
                reportParameter,
            )
            .onYesButtonClick((newReportParameter) => {
                this.fiLoadingService.trackPromise(this.statistic2Service.saveReportParameter(newReportParameter));
            });
    }

    onAddExistingReportClick($event) {
        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('statistic.add_existing_report'),
                yesButtonText: this.appTextService.get('done'),
                contentComponentRef: Statistic2AddExistingReportDialogComponent,
                contentComponentData: this.availableReportParameters,
                config: {
                    width: '400px',
                },
            })
            .onYesButtonClick((selectedReportParameter) => {
                selectedReportParameter.ShowOnDashboard = true;
                this.statistic2Service.saveReportParameter(selectedReportParameter);
            });
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
